/** Pieces shared by the connect screens: the workspace's sites, radio cards and connect errors. */
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { ErrorAlert, cx, buttonClass } from "@/components/ui";
import { portal } from "@/lib/api";
import { ApiError } from "@/lib/http";
import type { Site } from "@/lib/types";
import { Notice } from "@/components/domains/parts";

/** The workspace's live sites: the Weavex website first, then the sites uploaded on Hosting. */
export function useSites() {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: ["sites", orgId],
    queryFn: () => portal.sites(orgId),
    select: (r) =>
      r.sites
        .filter((s) => s.status !== "deleted" && s.status !== "pending_deletion")
        .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === "builder" ? -1 : 1)),
  });
}

/** Where a site's own custom domains are managed. */
export function siteDomainsPath(site: Pick<Site, "id" | "kind">): string {
  return site.kind === "external" ? `/hosting/sites/${site.id}` : `/websites/${site.id}?tab=domains`;
}

export function siteKindLabel(site: Pick<Site, "kind">): string {
  return site.kind === "builder" ? "Weavex website" : "Hosted site";
}

/** A selectable card with a coloured tile, as in "1. Choose a domain". */
export function RadioCard({
  name,
  checked,
  onSelect,
  tile,
  tileClass,
  title,
  detail,
  disabled,
}: {
  name: string;
  checked: boolean;
  onSelect: () => void;
  tile: ReactNode;
  tileClass: string;
  title: ReactNode;
  detail?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label
      className={cx(
        "flex cursor-pointer items-center gap-3 rounded-[11px] border p-3 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-500/30",
        checked ? "border-brand-500 bg-[#fef8f6]" : "border-border bg-surface hover:bg-[#fdfbf9]",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
        className="sr-only"
      />
      <span
        className={cx("flex size-9 shrink-0 items-center justify-center rounded-[9px] text-white", tileClass)}
      >
        {tile}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-semibold text-fg">{title}</span>
        {detail && <span className="text-xs text-fg-subtle">{detail}</span>}
      </span>
      <span
        aria-hidden
        className={cx(
          "flex size-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px]",
          checked ? "border-brand-500 bg-brand-500" : "border-[#c9c0bb] bg-surface",
        )}
      >
        {checked && <span className="size-1.5 rounded-full bg-white" />}
      </span>
    </label>
  );
}

/** Connect failures with somewhere to go: plan limits → plans, other nameservers → Nameservers tab. */
export function ConnectError({ error, domainId }: { error: unknown; domainId?: string }) {
  if (!(error instanceof ApiError)) return <ErrorAlert error={error} />;
  if (error.reason === "PLAN_LIMIT_REACHED")
    return (
      <Notice
        tone="warning"
        title="Your plan's custom domain limit is reached"
        actions={
          <Link to="/billing/plans" className={buttonClass("brand", "lg")}>
            See plans
          </Link>
        }
      >
        {error.message}
      </Notice>
    );
  if (error.reason === "DNS_NOT_MANAGED" && domainId)
    return (
      <Notice
        tone="warning"
        title="This domain uses other nameservers"
        actions={
          <Link to={`/domains/${domainId}?tab=nameservers`} className={buttonClass("secondary", "lg")}>
            Open nameservers
          </Link>
        }
      >
        {error.message}
      </Notice>
    );
  return <ErrorAlert error={error} />;
}
